import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import { SDOCS_IMPERSONATE_HEADER } from '@semeris/workspace-types';
import { catchError, defer, map, Observable, of, switchMap, throwError } from 'rxjs';

import { WORKSPACE_GATEWAY_URL } from './workspace-gateway-url';
import { ImpersonationDenial, WorkspaceImpersonationService } from '../../workspace-ui/workspace-impersonation.service';

// A Map, not an object: an object lookup resolves `constructor` / `toString` /
// `__proto__` to an inherited member, which is truthy and so survives a `?? null`.
const DENIAL_BY_MESSAGE = new Map<string, ImpersonationDenial>([
  ['Impersonation requires platform admin', 'not-admin'],
  ['Unknown impersonation target', 'unknown-target'],
]);

/**
 * The refusal message, if the body carries one. Async because a `responseType: 'blob'` request
 * (document and satellite downloads) delivers its error body as a Blob, readable only as a promise.
 */
function readMessage(body: unknown): Observable<string> {
  if (body instanceof Blob) {
    // `defer` so a missing/failing `text()` surfaces as an empty message rather than throwing
    // out of the interceptor's catchError.
    return defer(() => body.text()).pipe(
      map(parseMessage),
      catchError(() => of('')),
    );
  }
  return of(parseMessage(body));
}

/** Error bodies normally arrive parsed; a `responseType: 'text'` caller leaves them raw. */
function parseMessage(body: unknown): string {
  if (typeof body === 'string') {
    try {
      return parseMessage(JSON.parse(body));
    } catch {
      return '';
    }
  }
  return (body as { message?: string } | null)?.message ?? '';
}

/**
 * Names sdocs' impersonation target on every Workspace gateway call, so the embedded Workspace
 * screens follow sdocs instead of showing the admin's own data. On refusal a read is replayed
 * as the admin and the reason latched, rather than surfacing a blank screen.
 */
export const workspaceImpersonateInterceptor: HttpInterceptorFn = (req, next) => {
  if (!WORKSPACE_GATEWAY_URL.test(req.url)) {
    return next(req);
  }

  // Captured before the pipe — the operators below run outside the injection context.
  const impersonation = inject(WorkspaceImpersonationService);

  // Held until the profile settles, because that is when the target becomes knowable: a gateway
  // read sent before it resolves as the admin, with no error and — for the boot workspace list —
  // cached that way for the session. Emits synchronously once the profile is in the store, so
  // this costs nothing after boot, and a profile that never settles fails the request rather
  // than sending it as the admin. See `WorkspaceImpersonationService.whenResolved`.
  return impersonation.whenResolved().pipe(switchMap(() => sendAsTarget(req, next, impersonation)));
};

/** The impersonated send, with its refusal recovery. Split out only so the gate above reads as one line. */
function sendAsTarget(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  impersonation: WorkspaceImpersonationService,
): Observable<HttpEvent<unknown>> {
  const headers = impersonation.header();
  const target = headers[SDOCS_IMPERSONATE_HEADER];
  if (!target) {
    return next(req);
  }

  return next(req.clone({ setHeaders: headers })).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 403) {
        return throwError(() => err);
      }
      // Matched on the message, never on the 403 alone: acting as the target legitimately 403s
      // for whatever they cannot reach, and latching on those would end the impersonation.
      return readMessage(err.error).pipe(
        switchMap((message) => {
          const reason = DENIAL_BY_MESSAGE.get(message);
          if (!reason) {
            return throwError(() => err);
          }
          // The target this request carried — `impersonatedUser` may have swapped during the read.
          // The notice it raises, with its reload action, is the whole recovery: nothing is
          // re-sent as the admin, so no method needs classifying and no refused write can ever
          // execute under the wrong actor. The refusal reaches the caller, which fails one screen
          // rather than silently swapping whose data it shows.
          impersonation.markDenied(target, reason);
          return throwError(() => err);
        }),
      );
    }),
  );
}
